/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo, useRef } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import { Grid, Icon, Popover } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftPagination from "components/SoftPagination";
import DataTable from "../Tables/DataTable";

// 🔹 Filters – updated version
import UserFilters from "layouts/interviewSelected/components/useFilters";

// 🔹 Calendar
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// 🔹 Drawer
import OvertimeDrawer from "./OvertimeDrawer";

// 🔹 Zustand
import useOvertimeStore from "store/useOvertimeStore";

// 🔐 Auth
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";

// ✅ NEW hook – lazy-loaded dropdowns
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";

// Helper for leave status (optional)
import { getLeaveStatusInfo } from "layouts/leavetracker/helper";

// (SoftSelect might not be needed anymore, but keep if used elsewhere)
import SoftSelect from "components/SoftSelect";
import FlagLoader from "layouts/flagloader";

/* ================= PERMISSIONS ================= */
const PERMISSIONS = {
  ADD_OVERTIME: "Attendance.Overtime.History.AddOvertimeButton",
  BRANCH_FILTER: "Attendance.Overtime.History.BranchFilter",
  SHIFT_FILTER: "Attendance.Overtime.History.ShiftFilter",
  DEPARTMENT_FILTER: "Attendance.Overtime.History.DepartmentFilter",
  DESIGNATION_FILTER: "Attendance.Overtime.History.DesignationFilter",
  ROLE_FILTER: "Attendance.Overtime.History.RoleFilter",
  SEARCH_FILTER: "Attendance.Overtime.History.SearchFilter",
  FROM_DATE_FILTER: "Attendance.Overtime.History.FromDateFilter",
  TO_DATE_FILTER: "Attendance.Overtime.History.ToDateFilter",
  STATUS_FILTER: "Attendance.Overtime.History.StatusFilter",
};

/* ================= DEBOUNCE HOOK ================= */
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/* ================= CLEAN PARAMS ================= */
const cleanParams = (obj) => {
  const p = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== "" && v !== "all" && v !== null && v !== undefined) {
      p[k] = v;
    }
  });
  return p;
};

function OvertimeHistory() {
  /* ================= AUTH ================= */
  const { userPermission = [] } = useAuth();
  const canViewPage = true;
  const canCreateOvertime = hasPermission(userPermission, PERMISSIONS.ADD_OVERTIME);

  /* ================= STORE ================= */
  const { overtimeRequests, fetchOvertimeHistory } = useOvertimeStore();

  /* ================= DROPDOWNS – NEW ================= */
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchSubBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  /* ================= DRAWER STATE ================= */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ================= FILTER STATE ================= */
  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "all",
    from: "",
    to: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  /* ================= PAGINATION ================= */
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  /* ================= LOADING ================= */
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  /* ================= CALENDAR ================= */
  const [fromAnchor, setFromAnchor] = useState(null);
  const [toAnchor, setToAnchor] = useState(null);

  const lastQueryRef = useRef("");

  /* ================= FETCH ================= */
  const fetchOvertime = async (initial = false) => {
    const params = cleanParams({
      ...filters,
      q: debouncedSearch,
      page: pagination.current_page,
      per_page: pagination.per_page,
    });

    const queryKey = JSON.stringify(params);
    lastQueryRef.current = queryKey;

    try {
      initial ? setPageLoading(true) : setTableLoading(true);

      const res = await fetchOvertimeHistory(params);

      const meta = res?.meta || res; // API might return paginator at root

      if (meta && meta.current_page) {
        setPagination((p) => {
          if (
            p.current_page === meta.current_page &&
            p.last_page === meta.last_page &&
            p.total === meta.total &&
            p.per_page === meta.per_page
          ) {
            return p;
          }
          return {
            ...p,
            current_page: meta.current_page,
            last_page: meta.last_page,
            total: meta.total,
            per_page: meta.per_page,
          };
        });
      }
    } finally {
      setPageLoading(false);
      setTableLoading(false);
    }
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (canViewPage) fetchOvertime(true);
  }, [canViewPage]);

  /* ================= FILTER / SEARCH / PAGE ================= */
  useEffect(() => {
    if (canViewPage) fetchOvertime(false);
  }, [filters, debouncedSearch, pagination.current_page]);

  /* ================= FILTER HANDLER ================= */
  const handleFilterChange = (key, value) => {
    if (key === "__open_from__") {
      setFromAnchor(value);
      return;
    }
    if (key === "__open_to__") {
      setToAnchor(value);
      return;
    }

    setFilters((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  /* ================= HELPERS ================= */
  const formatDate = (d) => (d ? dayjs(d).format("DD-MMM-YYYY") : "N/A");
  const formatMinutes = (m) => {
    if (!m) return "0h";
    const h = Math.floor(m / 60);
    const min = m % 60;
    return min ? `${h}h ${min}m` : `${h}h`;
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = useMemo(
    () => [
      { Header: "Employee", accessor: "username" },
      { Header: "Branch", accessor: "branch" },
      { Header: "Shift Time", accessor: "shiftTime" },
      { Header: "OT Date", accessor: "attendanceDate" },
      { Header: "OT Time", accessor: "overtimeTime" },
      { Header: "OT (Min)", accessor: "totalOvertime" },
      { Header: "Break (Min)", accessor: "breakMinutes" },
      { Header: "Login IP", accessor: "loginIp" },
      { Header: "Logout IP", accessor: "logoutIp" },
      { Header: "Requested On", accessor: "requested" },
    ],
    []
  );


  const getPaginationRange = () => {
    const total = pagination.last_page;
    const current = pagination.current_page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  // const rows = overtimeRequests.map((r) => ({
  //   username: r.user?.name || "-",
  //   branch: r.branch?.name || "-",
  //   shiftTime: r.shift ? `${r.shift.start_time} - ${r.shift.end_time}` : "-",
  //   attendanceDate: formatDate(r.attendance_date),
  //   overtimeTime:
  //     r.overtime_start && r.overtime_end
  //       ? `${dayjs(r.overtime_start).format("HH:mm")} - ${dayjs(r.overtime_end).format("HH:mm")}`
  //       : "-",
  //   totalOvertime: formatMinutes(r.total_overtime_minutes),
  //   breakMinutes: formatMinutes(r.total_break_minutes),
  //   loginIp: r.login_ip || "-",
  //   logoutIp: r.logout_ip || "-",
  //   requested: formatDate(r.created_at),
  // }));

  const rows = useMemo(() => {
    return overtimeRequests.map((r) => ({
      // username: r.user?.name || "-",
      username: `${r?.user?.name || ""} ${r?.user?.last_name || ""}`.trim() || "N/A",
      branch: r.branch?.name || "-",
      shiftTime: r.shift ? `${r.shift.start_time} - ${r.shift.end_time}` : "-",
      attendanceDate: formatDate(r.attendance_date),
      overtimeTime:
        r.overtime_start && r.overtime_end
          ? `${dayjs(r.overtime_start).format("HH:mm")} - ${dayjs(r.overtime_end).format("HH:mm")}`
          : "-",
      totalOvertime: formatMinutes(r.total_overtime_minutes),
      breakMinutes: formatMinutes(r.total_break_minutes),
      loginIp: r.login_ip || "-",
      logoutIp: r.logout_ip || "-",
      requested: formatDate(r.created_at),
    }));
  }, [overtimeRequests]); // ✅ only recompute when overtimeRequests changes


  /* ================= LOADING GUARD ================= */
  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <FlagLoader /> */}
        <FlagLoader />
      </SoftBox>
    );
  }

  /* ================= UI ================= */
  return (
    <Card>
      <SoftBox py={1}>
        {/* ✅ UPDATED FILTERS – with new props */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchSubBranches={fetchSubBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters={true}
          pageContext="Attendance.Overtime.History"
        />

        {/* HEADER */}
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          mt={1}
          mb={-1}
        >
          <Grid item>
            <SoftTypography variant="h6" color="text">
              Overtime History ({pagination.total})
            </SoftTypography>
          </Grid>

          {/* Optional status filter – if you want it, uncomment and use SoftSelect */}
          {/* <Grid item xs={12} sm={4} md={3}>
            <SoftSelect
              options={[
                { value: "", label: "All Status" },
                { value: "approved", label: "Approved" },
                { value: "pending", label: "Pending" },
                { value: "rejected", label: "Rejected" },
              ]}
              placeholder="Status"
              value={
                [
                  { value: "", label: "All Status" },
                  { value: "approved", label: "Approved" },
                  { value: "pending", label: "Pending" },
                  { value: "rejected", label: "Rejected" },
                ].find((o) => o.value === filters.status) || { value: "", label: "All Status" }
              }
              onChange={(option) => handleFilterChange("status", option.value)}
            />
          </Grid> */}

          {canCreateOvertime && (
            <Grid item>
              <SoftButton
                color="info"
                variant="gradient"
                startIcon={<AddIcon />}
                onClick={() => setDrawerOpen(true)}
              >
                Add Overtime
              </SoftButton>
            </Grid>
          )}
        </Grid>

        {/* TABLE */}
        {tableLoading ? (
          <SoftBox py={4} textAlign="center">
            {/* <CircularProgress /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            entriesPerPage={false}
            canSearch={false}
            showTotalEntries
          />
        )}

        {/* PAGINATION */}
        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              {/* {Array.from({ length: pagination.last_page }, (_, i) => (
                <SoftPagination
                  key={i}
                  item
                  active={pagination.current_page === i + 1}
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      current_page: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </SoftPagination>
              ))} */}

              {/* FIRST */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                «
              </SoftPagination>

              {/* PREV */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page - 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ‹
              </SoftPagination>

              {/* PAGE NUMBERS */}
              {getPaginationRange().map((item, i) => {
                if (item === "...") {
                  return (
                    <SoftPagination key={i} item disabled>
                      ...
                    </SoftPagination>
                  );
                }

                return (
                  <SoftPagination
                    key={i}
                    item
                    active={pagination.current_page === item}
                    onClick={() => {
                      setPagination((p) => ({
                        ...p,
                        current_page: item,
                      }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {item}
                  </SoftPagination>
                );
              })}

              {/* NEXT */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page + 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ›
              </SoftPagination>

              {/* LAST */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: pagination.last_page,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                »
              </SoftPagination>

            </SoftPagination>
          </SoftBox>
        )}
      </SoftBox>

      {/* FROM DATE CALENDAR */}
      <Popover open={Boolean(fromAnchor)} anchorEl={fromAnchor} onClose={() => setFromAnchor(null)}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            onChange={(d) => {
              handleFilterChange("from", d.format("YYYY-MM-DD"));
              setFromAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* TO DATE CALENDAR */}
      <Popover open={Boolean(toAnchor)} anchorEl={toAnchor} onClose={() => setToAnchor(null)}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            onChange={(d) => {
              handleFilterChange("to", d.format("YYYY-MM-DD"));
              setToAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* DRAWER */}
      {canCreateOvertime && (
        <OvertimeDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            fetchOvertime(true); // refresh after adding
          }}
        />
      )}
    </Card>
  );
}

export default OvertimeHistory;